import { KEYED_TRANSCRIPTION_PROVIDERS } from "@lexicanon/shared"
import type { TSettingsGroup } from "./groups"

/**
 * Maps every raw setting KEY to one of the settings sections so the super-admin
 * company-override table can render grouped, searchable sections that mirror the
 * tenant settings rail (`groups.ts`). Tenant + admin stay in sync because both
 * read the same `TSettingsGroup` values (and `ADMIN_SETTINGS_GROUP_META`,
 * which lives in `groups.ts` alongside the tenant rail meta).
 *
 * The admin table adds a sixth catch-all group, `platform`, for keys with no
 * tenant-facing card: platform-only defaults (`default_organization_id`), the
 * `default_*` platform-fallback orphans that shadow an org key, and internal or
 * advanced operator toggles (`byok_enabled`, `translation_adapter`,
 * `org_encryption_at_rest`, budget-reset bookkeeping). Anything not explicitly
 * mapped falls through to `platform`, so a brand-new key surfaces there rather
 * than vanishing.
 *
 * Deliberately macro-free (only a type-only import of `TSettingsGroup`) so the
 * resolver stays unit-testable — the repo's Vitest config doesn't transform
 * Lingui macros, so any `msg`-bearing module can't be imported from a plain test.
 */
export const PLATFORM_GROUP = "platform" as const
export type TSettingsKeyGroup = TSettingsGroup | typeof PLATFORM_GROUP

/**
 * Transcription BYOK key settings, derived from the shared catalog so a newly
 * added keyed provider lands in Advanced automatically. Covers
 * the `platform_<id>_key`, `org_<id>_key`, and legacy unprefixed `<id>_key` forms.
 */
const TRANSCRIPTION_KEY_GROUP: Record<string, TSettingsGroup> = {}
for (const provider of KEYED_TRANSCRIPTION_PROVIDERS) {
  TRANSCRIPTION_KEY_GROUP[`platform_${provider.id}_key`] = "advanced"
  TRANSCRIPTION_KEY_GROUP[`org_${provider.id}_key`] = "advanced"
  TRANSCRIPTION_KEY_GROUP[`${provider.id}_key`] = "advanced"
}

/**
 * Explicit key → group map for every setting with a tenant-facing home. Keys
 * absent here (and not caught by the `task_model_*` rule or the transcription
 * key set) resolve to `platform` via `groupForSettingKey`.
 */
const SETTINGS_KEY_GROUP: Record<string, TSettingsGroup> = {
  // General — languages, cleanup, company words, the member-facing locale.
  default_language: "general",
  org_custom_vocab: "general",
  auto_translate_to: "general",
  transcript_cleanup_enabled: "general",
  default_ui_locale: "general",

  // Reports — report gating and the meeting summary layout. Image generation
  // is an Advanced card, so its keys live there.
  summary_layout: "reports",
  reports_best_model_enabled: "reports",

  // Integrations — the workspace's own Microsoft Teams config.
  teams_tenant_id: "integrations",
  teams_app_id: "integrations",
  teams_secret: "integrations",
  teams_watched_user_ids: "integrations",
  teams_webhook_url: "integrations",

  // Advanced — transcription, speakers, models, the prompt, images, limits.
  batch_fallback_order: "advanced",
  streaming_fallback_order: "advanced",
  speaker_confirm_threshold: "advanced",
  auto_identify_speakers: "advanced",
  echo_suppression_enabled: "advanced",
  diarization_provider: "advanced",
  transcription_mode: "advanced",
  ensemble_merge_mode: "advanced",
  ensemble_word_engines: "advanced",
  ensemble_turn_engines: "advanced",
  ensemble_adjudicate_speakers: "advanced",
  ensemble_headcount_tie: "advanced",
  platform_azure_region: "advanced",
  org_azure_region: "advanced",
  azure_region: "advanced",
  platform_mai_region: "advanced",
  org_mai_region: "advanced",
  mai_region: "advanced",
  system_prompt: "advanced",
  platform_anthropic_key: "advanced",
  org_anthropic_key: "advanced",
  platform_openrouter_key: "advanced",
  org_openrouter_key: "advanced",
  platform_openai_key: "advanced",
  org_openai_key: "advanced",
  default_model: "advanced",
  light_model: "advanced",
  heavy_model: "advanced",
  final_analysis_input_budget: "advanced",
  quick_analysis_input_budget: "advanced",
  speaker_resolution_input_budget: "advanced",
  platform_runware_image_key: "advanced",
  org_runware_image_key: "advanced",
  image_provider: "advanced",
  image_model: "advanced",
  visual_generation_enabled: "advanced",
  visual_style: "advanced",
  meeting_cost_hourly_rate_eur: "advanced",
}

/**
 * Resolve a raw setting key to its group. `task_model_*` keys route to Advanced
 * (covers every current and future `TLLMAction`); the transcription key set and the
 * explicit map handle the rest; unmapped keys land in `platform`.
 */
export function groupForSettingKey(key: string): TSettingsKeyGroup {
  if (key.startsWith("task_model_")) return "advanced"
  return SETTINGS_KEY_GROUP[key] ?? TRANSCRIPTION_KEY_GROUP[key] ?? PLATFORM_GROUP
}
